import { observer } from 'mobx-react-lite';
import React, { useEffect, useMemo, useState } from 'react';
import { Stack, Typography, Divider, Box, Button, Alert, Accordion, AccordionSummary, AccordionDetails, Radio, RadioGroup, FormControlLabel, FormControl, CircularProgress } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { CopilotStore } from '../../CopilotStore';
import { DocumentTag, IngestFile, Tag, TagConfig } from '../../models';
import { SIAutocomplete, SIAutocompleteMultiSelect, SelectionItem } from '../../../controls/Autocomplete';
import { CSVDownloadButton } from '../../../controls/CSVDownloadButton';
import { CSVUploadButton, CSVSchema } from '../../../controls/CSVUploadButton';
import StepConfirmationWithState, { ConfirmationDialogPropsWithState } from '../../../controls/StepConfirmationWithState';
import { normalizeTagLabel } from './tagConfigLookups';

type TagManagementDocTagsProps = {
    store: CopilotStore
}

const DOCUMENT_TAGS_UPDATE_BATCH_SIZE = 2000;

export const TagManagementDocTags = observer(({ store }: TagManagementDocTagsProps) => {
    const {
        selectedCustomer: customer,
        ingestFiles,
        filesLoading,
        activeTagValues,
        tagValueByRowId,
        tagValuesByTagIndexAndValue,
        tagConfigByIndex,
        tagConfigByLabel,
        fileById,
        fileByDocumentNameOrId,
    } = store;

    const [updateLoading, setUpdateLoading] = useState<boolean>(false);
    const [resetKey, setResetKey] = useState<number>(0);
    
    // Update Document Tags state
    const [selectedDocuments, setSelectedDocuments] = useState<IngestFile[]>([]);
    const [tagsToAdd, setTagsToAdd] = useState<Tag[]>([]);
    const [tagsToRemove, setTagsToRemove] = useState<Tag[]>([]);

    // Tag Documents Where state
    const [whereTagsToAdd, setWhereTagsToAdd] = useState<Tag[]>([]);
    const [whereTaggedWith, setWhereTaggedWith] = useState<Tag | null>(null);
    const [whereLoading, setWhereLoading] = useState<boolean>(false);

    // Dialog state for notifications and confirmations
    const [dialogProps, setDialogProps] = useState<ConfirmationDialogPropsWithState>();

    // CSV schema and handlers for Update Document Tags
    const docTagsCsvSchema: CSVSchema = {
        fields: [
            { name: 'document_name', required: true, type: 'string' },
            { name: 'tag_config_label', required: true, type: 'string' },
            { name: 'tag_value', required: true, type: 'string' },
        ],
        allowExtraFields: false,
        minRows: 1,
    };

    const generateDocTagsCSV = (includeUntagged: boolean = false): string => {
        const header = 'document_name,tag_config_label,tag_value';
        const rows: string[] = [];
        const escapeCsv = (value: string) => value.includes(',') ? `"${value}"` : value;
        
        ingestFiles.forEach(doc => {
            if (doc.tags && doc.tags.length > 0) {
                doc.tags.forEach(tag => {
                    const tagConfig = tagConfigByIndex.get(String(tag.tag_index));
                    if (tagConfig) {
                        rows.push(`${escapeCsv(doc.document_name || doc.id)},${escapeCsv(tagConfig.tag_label || '')},${escapeCsv(tag.value || '')}`);
                    }
                });
            } else if (includeUntagged) {
                // Add row for untagged documents with empty tag fields
                rows.push(`${escapeCsv(doc.document_name || doc.id)},,`);
            }
        });
        
        return [header, ...rows].join('\n');
    };

    const handleDocTagsCSVUpload = async (data: any[], errors: string[]) => {
        if (errors.length > 0) return;

        // Validation
        const invalidDocNames: string[] = [];
        const invalidTagConfigs: string[] = [];
        const invalidTagValues: string[] = [];
        
        const validRows: Array<{
            document: IngestFile;
            tagConfig: TagConfig;
            tagValue: Tag;
        }> = [];

        for (const row of data) {
            const docName = String(row.document_name || '').trim();
            const tagConfigLabel = String(row.tag_config_label || '').trim();
            const tagValueText = String(row.tag_value || '').trim();

            // Validate document name
            const document = fileByDocumentNameOrId.get(docName);
            if (!document) {
                invalidDocNames.push(docName);
                continue;
            }

            // Validate tag config label
            const tagConfig = tagConfigByLabel.get(normalizeTagLabel(tagConfigLabel));
            if (!tagConfig) {
                invalidTagConfigs.push(tagConfigLabel);
                continue;
            }

            // Validate tag value exists for this config
            const tagValue = tagValuesByTagIndexAndValue
                .get(`${tagConfig.tag_index}|${tagValueText.toLowerCase()}`)
                ?.[0];
            if (!tagValue) {
                invalidTagValues.push(`"${tagConfigLabel}": "${tagValueText}"`);
                continue;
            }

            validRows.push({ document, tagConfig, tagValue });
        }

        // Show validation errors if any
        if (invalidDocNames.length > 0 || invalidTagConfigs.length > 0 || invalidTagValues.length > 0) {
            const errorMessages: string[] = [];
            if (invalidDocNames.length > 0) {
                errorMessages.push(`Unknown document names: ${Array.from(new Set(invalidDocNames)).join(', ')}`);
            }
            if (invalidTagConfigs.length > 0) {
                errorMessages.push(`Unknown tag config labels: ${Array.from(new Set(invalidTagConfigs)).join(', ')}`);
            }
            if (invalidTagValues.length > 0) {
                errorMessages.push(`Unknown tag values: ${Array.from(new Set(invalidTagValues)).join(', ')}`);
            }
            
            setDialogProps({
                title: 'CSV Validation Failed',
                content: () => (
                    <Alert severity="error" style={{ whiteSpace: 'pre-line' }}>
                        {errorMessages.join('\n')}
                    </Alert>
                ),
                onClose: () => setDialogProps(undefined),
                closeLabel: 'OK'
            });
            return;
        }

                if (validRows.length === 0) {
            setDialogProps({
                title: 'No Valid Data',
                content: () => (
                    <Alert severity="warning">
                        No valid tag assignments found in the CSV file.
                    </Alert>
                ),
                onClose: () => setDialogProps(undefined),
                closeLabel: 'OK'
            });
            return;
        }

        // Show mode selection dialog first
        showModeSelectionDialog(validRows);
    };

    const showModeSelectionDialog = (validRows: Array<{ document: IngestFile; tagConfig: TagConfig; tagValue: Tag; }>) => {
        setDialogProps({
            title: 'Choose Update Mode',
            content: (state, setState) => (
                <div>
                    <Typography variant="body1" style={{ marginBottom: 20 }}>
                        How would you like to apply the CSV updates?
                    </Typography>
                    <FormControl component="fieldset">
                        <RadioGroup
                            value={state?.mode || 'add'}
                            onChange={(e) => setState({ ...state, mode: e.target.value })}
                            style={{ gap: '16px' }}
                        >
                            <FormControlLabel 
                                value="add" 
                                control={<Radio />} 
                                label={
                                    <div>
                                        <Typography variant="body2" style={{ fontWeight: 500 }}>
                                            Add new tags only
                                        </Typography>
                                        <Typography variant="caption" style={{ color: '#666', display: 'block' }}>
                                            Only add tags from CSV that don't already exist on documents. Keep all existing tags.
                                        </Typography>
                                    </div>
                                } 
                            />
                            <FormControlLabel 
                                value="replace" 
                                control={<Radio />} 
                                label={
                                    <div>
                                        <Typography variant="body2" style={{ fontWeight: 500 }}>
                                            Replace all tags
                                        </Typography>
                                        <Typography variant="caption" style={{ color: '#666', display: 'block' }}>
                                            Set document tags to exactly match the CSV. Remove any existing tags not found in CSV.
                                        </Typography>
                                    </div>
                                } 
                            />
                        </RadioGroup>
                    </FormControl>
                </div>
            ),
            onYes: (state) => processCSVWithMode(validRows, state?.mode || 'add'),
            onClose: () => setDialogProps(undefined),
            yesLabel: 'Continue',
            closeLabel: 'Cancel'
        });
    };

    const processCSVWithMode = (validRows: Array<{ document: IngestFile; tagConfig: TagConfig; tagValue: Tag; }>, mode: string) => {
        if (mode === 'add') {
            processAddOnlyMode(validRows);
        } else {
            processReplaceMode(validRows);
        }
    };

    const processAddOnlyMode = (validRows: Array<{ document: IngestFile; tagConfig: TagConfig; tagValue: Tag; }>) => {
        // Group by document and prepare updates (only for tags that don't already exist)
        const documentUpdates = new Map<string, { document: IngestFile; addTagIds: Set<string> }>();
        const skippedAssignments: string[] = [];
        
        validRows.forEach(({ document, tagConfig, tagValue }) => {
            // Check if document already has this tag
            const existingTagIds = new Set(document.tags?.map(tag => tag.rowid) || []);
            
            if (existingTagIds.has(tagValue.rowid)) {
                skippedAssignments.push(`${document.document_name || document.id}: ${tagConfig.tag_label} - ${tagValue.value}`);
                return; // Skip this assignment as it already exists
            }
            
            if (!documentUpdates.has(document.id)) {
                documentUpdates.set(document.id, {
                    document,
                    addTagIds: new Set()
                });
            }
            documentUpdates.get(document.id)!.addTagIds.add(tagValue.rowid);
        });

        // If no updates needed, show info message
        if (documentUpdates.size === 0) {
            setDialogProps({
                title: 'No Updates Needed',
                content: () => (
                    <Alert severity="info">
                        All tag assignments in the CSV already exist on the specified documents.
                    </Alert>
                ),
                onClose: () => setDialogProps(undefined),
                closeLabel: 'OK'
            });
            return;
        }

        // Show confirmation dialog for add-only mode
        showConfirmationDialog(documentUpdates, skippedAssignments, 'add');
    };

    const processReplaceMode = (validRows: Array<{ document: IngestFile; tagConfig: TagConfig; tagValue: Tag; }>) => {
        // Group by document and prepare full replacements
        const documentUpdates = new Map<string, { document: IngestFile; addTagIds: Set<string>; removeTagIds: Set<string> }>();
        
        // First, collect all CSV tags by document
        const csvTagsByDocument = new Map<string, Set<string>>();
        validRows.forEach(({ document, tagValue }) => {
            if (!csvTagsByDocument.has(document.id)) {
                csvTagsByDocument.set(document.id, new Set());
            }
            csvTagsByDocument.get(document.id)!.add(tagValue.rowid);
        });

        // Process each document that appears in CSV
        csvTagsByDocument.forEach((csvTagIds, documentId) => {
            const document = fileById.get(documentId);
            if (!document) return;

            const existingTagIds = new Set(document.tags?.map(tag => tag.rowid) || []);
            const addTagIds = new Set<string>();
            const removeTagIds = new Set<string>();

            // Tags to add: in CSV but not on document
            csvTagIds.forEach(tagId => {
                if (!existingTagIds.has(tagId)) {
                    addTagIds.add(tagId);
                }
            });

            // Tags to remove: on document but not in CSV
            existingTagIds.forEach(tagId => {
                if (!csvTagIds.has(tagId)) {
                    removeTagIds.add(tagId);
                }
            });

            // Only include documents that need changes
            if (addTagIds.size > 0 || removeTagIds.size > 0) {
                documentUpdates.set(documentId, {
                    document,
                    addTagIds,
                    removeTagIds
                });
            }
        });

        // Show confirmation dialog for replace mode
        showConfirmationDialog(documentUpdates, [], 'replace');
    };

    const showConfirmationDialog = (documentUpdates: Map<string, any>, skippedAssignments: string[], mode: string) => {
        const totalDocuments = documentUpdates.size;
        
        if (totalDocuments === 0) {
            const message = mode === 'add' 
                ? 'All tag assignments in the CSV already exist on the specified documents.'
                : 'All documents already have the exact tags specified in the CSV.';
            
            setDialogProps({
                title: 'No Updates Needed',
                content: () => (
                    <Alert severity="info">
                        {message}
                    </Alert>
                ),
                onClose: () => setDialogProps(undefined),
                closeLabel: 'OK'
            });
            return;
        }

        const totalTagAssignments = Array.from(documentUpdates.values()).reduce((sum, doc) => sum + doc.addTagIds.size, 0);
        const totalTagRemovals = mode === 'replace' 
            ? Array.from(documentUpdates.values()).reduce((sum, doc) => sum + (doc.removeTagIds?.size || 0), 0)
            : 0;

        setDialogProps({
            title: 'Confirm Tag Updates',
            content: () => (
                <div>
                    <Typography variant="body1" style={{ marginBottom: 15 }}>
                        The following updates will be applied:
                    </Typography>
                    <Typography variant="body1" style={{ marginLeft: 20, marginBottom: 10 }}>
                        • {totalDocuments} document(s) will be updated
                    </Typography>
                    {totalTagAssignments > 0 && (
                        <Typography variant="body1" style={{ marginLeft: 20, marginBottom: 10 }}>
                            • {totalTagAssignments} tag(s) will be added
                        </Typography>
                    )}
                    {totalTagRemovals > 0 && (
                        <Typography variant="body1" style={{ marginLeft: 20, marginBottom: 10 }}>
                            • {totalTagRemovals} tag(s) will be removed
                        </Typography>
                    )}
                    {skippedAssignments.length > 0 && (
                        <Alert severity="info" style={{ marginBottom: 15, marginTop: 10 }}>
                            {skippedAssignments.length} tag assignment(s) were skipped because they already exist.
                        </Alert>
                    )}
                    
                    <Accordion style={{ marginBottom: 15 }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="body2" style={{ fontWeight: 500 }}>
                                View detailed updates
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                {Array.from(documentUpdates.values()).map(({ document, addTagIds, removeTagIds }) => (
                                    <div key={document.id} style={{ marginBottom: 16 }}>
                                        <Typography variant="body2" style={{ fontWeight: 500, marginBottom: 8 }}>
                                            {document.document_name || document.id}
                                        </Typography>
                                        <div style={{ marginLeft: 16 }}>
                                            {Array.from(addTagIds).map(tagId => {
                                                const tagIdValue = String(tagId);
                                                const tag = tagValueByRowId.get(tagIdValue);
                                                const config = tagConfigByIndex.get(String(tag?.tag_index));
                                                return (
                                                    <Typography key={`add-${tagIdValue}`} variant="body2" style={{ color: '#4caf50', marginBottom: 4 }}>
                                                        + {config?.tag_label}: {tag?.value}
                                                    </Typography>
                                                );
                                            })}
                                            {removeTagIds && Array.from(removeTagIds).map(tagId => {
                                                const tagIdValue = String(tagId);
                                                const tag = tagValueByRowId.get(tagIdValue);
                                                const config = tagConfigByIndex.get(String(tag?.tag_index));
                                                return (
                                                    <Typography key={`remove-${tagIdValue}`} variant="body2" style={{ color: '#f44336', marginBottom: 4 }}>
                                                        - {config?.tag_label}: {tag?.value}
                                                    </Typography>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </AccordionDetails>
                    </Accordion>
                    
                    <Typography variant="body1">
                        Do you want to proceed?
                    </Typography>
                </div>
            ),
            onYes: () => executeCSVUpdates(Array.from(documentUpdates.values())),
            onClose: () => setDialogProps(undefined),
            yesLabel: 'Update Tags',
            closeLabel: 'Cancel'
        });
    };

    const batchUpdateDocumentTags = async (docTagsToRemove: DocumentTag[], docTagsToAdd: DocumentTag[]) => {
        const totalUpdates = docTagsToRemove.length + docTagsToAdd.length;
        if (totalUpdates < DOCUMENT_TAGS_UPDATE_BATCH_SIZE) {
            await store.updateDocumentTags(docTagsToRemove, docTagsToAdd);
        } else {
            const setDialogPropsFn = (progress: number) => {
                setDialogProps({
                    title: 'Updating document tags',
                    content: () => (
                        <Alert severity="error">
                            DO NOT CLOSE THIS PAGE UNTIL THE UPDATE IS COMPLETE. Progress: {progress.toFixed(2)}%
                        </Alert>
                    ),
                    onClose: () => setDialogProps(undefined),
                    closeLabel: 'OK'
                })};
            let completedUpdates = 0;
            for (let i = 0; i < docTagsToRemove.length; i += DOCUMENT_TAGS_UPDATE_BATCH_SIZE) {
                const batch = docTagsToRemove.slice(i, i + DOCUMENT_TAGS_UPDATE_BATCH_SIZE);
                await store.updateDocumentTags(batch, undefined);
                completedUpdates += batch.length;
                setDialogPropsFn(completedUpdates / totalUpdates * 100);
            }
            for (let i = 0; i < docTagsToAdd.length; i += DOCUMENT_TAGS_UPDATE_BATCH_SIZE) {
                const batch = docTagsToAdd.slice(i, i + DOCUMENT_TAGS_UPDATE_BATCH_SIZE);
                await store.updateDocumentTags(undefined, batch);
                completedUpdates += batch.length;
                setDialogPropsFn(completedUpdates / totalUpdates * 100);
            }
            setDialogProps(undefined);
        }
    }

    const executeCSVUpdates = async (documentUpdates: Array<{ document: IngestFile; addTagIds: Set<string>; removeTagIds?: Set<string> }>) => {
        setUpdateLoading(true);
        setDialogProps(undefined);
        
        try {
            const docTagsToRemove: DocumentTag[] = [];
            const docTagsToAdd: DocumentTag[] = [];

            documentUpdates.forEach(({ document, addTagIds, removeTagIds }) => {
                removeTagIds?.forEach(tagId => {
                    docTagsToRemove.push({
                        document_id: document.id,
                        tag_value_id: tagId
                    });
                });
                addTagIds.forEach(tagId => {
                    docTagsToAdd.push({
                        document_id: document.id,
                        tag_value_id: tagId
                    });
                });
            });

            await batchUpdateDocumentTags(docTagsToRemove, docTagsToAdd);
            
            setSelectedDocuments([]);
            setTagsToAdd([]);
            setTagsToRemove([]);
            setResetKey(prev => prev + 1);
            store.refreshFiles();
            
            // Show success message
            setDialogProps({
                title: 'Success',
                content: () => (
                    <Typography variant="body1">
                        Document tags updated successfully from CSV!
                    </Typography>
                ),
                onClose: () => setDialogProps(undefined),
                closeLabel: 'OK'
            });
        } catch (error) {
            console.error('Error updating document tags from CSV:', error);
            setDialogProps({
                title: 'Error',
                content: () => (
                    <Alert severity="error">
                        {error instanceof Error ? error.message : 'Failed to update document tags from CSV'}
                    </Alert>
                ),
                onClose: () => setDialogProps(undefined),
                closeLabel: 'OK'
            });
        } finally {
            setUpdateLoading(false);
        }
    };

    // Load data when customer changes
    useEffect(() => {
        resetForm();
    }, [customer]);

    // Reset form when customer changes
    const resetForm = () => {
        setSelectedDocuments([]);
        setTagsToAdd([]);
        setTagsToRemove([]);
        
        setWhereTagsToAdd([]);
        setWhereTaggedWith(null);
        
        setResetKey(prev => prev + 1); // Force MultiAutocomplete components to reset
    };

    // Get unique tags from selected documents
    const availableTagsToRemove = useMemo((): Tag[] => {
        if (selectedDocuments.length === 0) return [];
        
        const uniqueTagIds = new Set<string>();
        const uniqueTags: Tag[] = [];
        
        selectedDocuments.forEach(doc => {
            doc.tags?.forEach(tag => {
                if (!uniqueTagIds.has(tag.rowid)) {
                    uniqueTagIds.add(tag.rowid);
                    uniqueTags.push(tag);
                }
            });
        });
        
        return uniqueTags;
    }, [selectedDocuments]);

    const handleUpdateDocumentTags = async () => {
        if (!customer?.id || selectedDocuments.length === 0) return;
        
        setUpdateLoading(true);
        
        try {
            const docTagsToRemove: DocumentTag[] = [];
            const docTagsToAdd: DocumentTag[] = [];
            
            selectedDocuments.forEach(doc => {
                tagsToRemove.forEach(tag => {
                    docTagsToRemove.push({
                        document_id: doc.id,
                        tag_value_id: tag.rowid
                    });
                });
                tagsToAdd.forEach(tag => {
                    docTagsToAdd.push({
                        document_id: doc.id,
                        tag_value_id: tag.rowid
                    });
                });
            });

            await batchUpdateDocumentTags(docTagsToRemove, docTagsToAdd);
            
            setSelectedDocuments([]);
            setTagsToAdd([]);
            setTagsToRemove([]);
            setResetKey(prev => prev + 1); // Force MultiAutocomplete components to reset
            
            // Refresh documents to get updated tag information
            store.refreshFiles();
            
            // Show success message
            setDialogProps({
                title: 'Success',
                content: () => (
                    <Typography variant="body1">
                        Document tags updated successfully!
                    </Typography>
                ),
                onClose: () => setDialogProps(undefined),
                closeLabel: 'OK'
            });
            
            console.log('Document tags updated successfully');
        } catch (error) {
            console.error('Error updating document tags:', error);
            setDialogProps({
                title: 'Error',
                content: () => (
                    <Alert severity="error">
                        {error instanceof Error ? error.message : 'Failed to update document tags'}
                    </Alert>
                ),
                onClose: () => setDialogProps(undefined),
                closeLabel: 'OK'
            });
        } finally {
            setUpdateLoading(false);
        }
    };

    const isUpdateDisabled = () => {
        return !customer?.id || 
               selectedDocuments.length === 0 || 
               (tagsToAdd.length === 0 && tagsToRemove.length === 0) ||
               updateLoading ||
               whereLoading;
    };

    const handleUpdateDocumentTagsWhere = async () => {
        if (!customer?.id || whereTagsToAdd.length === 0 || !whereTaggedWith) return;
        
        setWhereLoading(true);
        
        try {
            const addTagValueIds = whereTagsToAdd.map(tag => tag.rowid);
            const whereTagValueId = whereTaggedWith.rowid;

            await store.updateDocumentTagsWhere(addTagValueIds, whereTagValueId);
            
            setWhereTagsToAdd([]);
            setWhereTaggedWith(null);
            setResetKey(prev => prev + 1); // Force MultiAutocomplete components to reset
            
            // Refresh documents to get updated tag information
            store.refreshFiles();
            
            // Show success message
            setDialogProps({
                title: 'Success',
                content: () => (
                    <Typography variant="body1">
                        Document tags updated successfully!
                    </Typography>
                ),
                onClose: () => setDialogProps(undefined),
                closeLabel: 'OK'
            });
            
            console.log('Document tags updated successfully using where clause');
        } catch (error) {
            console.error('Error updating document tags where:', error);
            setDialogProps({
                title: 'Error',
                content: () => (
                    <Alert severity="error">
                        {error instanceof Error ? error.message : 'Failed to update document tags'}
                    </Alert>
                ),
                onClose: () => setDialogProps(undefined),
                closeLabel: 'OK'
            });
        } finally {
            setWhereLoading(false);
        }
    };

    const isWhereUpdateDisabled = () => {
        return !customer?.id || 
               whereTagsToAdd.length === 0 || 
               !whereTaggedWith ||
               whereLoading ||
               updateLoading;
    };

    const isDisabled = !customer?.id || updateLoading || whereLoading || filesLoading;
    const isWhereDisabled = !customer?.id || updateLoading || whereLoading;

    // Helper functions to convert between objects and SelectionItems
    const documentsToSelectionItems = (docs: IngestFile[]): SelectionItem[] => {
        return docs.map(doc => ({ 
            value: doc.id, 
            label: doc.document_name || doc.id 
        }));
    };

    const tagsToSelectionItems = (tags: Tag[]): SelectionItem[] => {
        return tags.map(tag => ({ 
            value: tag.rowid, 
            label: tag.value 
        }));
    };

    const activeTagSelectionItems = useMemo(
        () => tagsToSelectionItems(activeTagValues),
        [activeTagValues],
    );

    const activeTagValueByRowId = useMemo(
        () => new Map(activeTagValues.map(tag => [tag.rowid, tag])),
        [activeTagValues],
    );

    const availableTagsToRemoveSelectionItems = useMemo(
        () => tagsToSelectionItems(availableTagsToRemove),
        [availableTagsToRemove],
    );

    const availableTagsToRemoveByRowId = useMemo(
        () => new Map(availableTagsToRemove.map(tag => [tag.rowid, tag])),
        [availableTagsToRemove],
    );

    const documentSelectionItems = useMemo(
        () => documentsToSelectionItems(ingestFiles),
        [ingestFiles],
    );

    const findDocumentsBySelectionItems = (items: SelectionItem[]): IngestFile[] => {
        return items
            .map(item => fileById.get(item.value))
            .filter((doc): doc is IngestFile => doc !== undefined);
    };

    const findTagsBySelectionItems = (items: SelectionItem[], sourceTagByRowId: Map<string, Tag>): Tag[] => {
        return items
            .map(item => sourceTagByRowId.get(item.value))
            .filter((tag): tag is Tag => tag !== undefined);
    };

    const findTagBySelectionItem = (item: SelectionItem | undefined): Tag | null => {
        if (!item) return null;
        return tagValueByRowId.get(item.value) || null;
    };

    const loadProgress = () => {
        return Boolean(customer?.id);
    };

    return (
        <Stack direction='row' flex={1} marginTop='16px' spacing={3} className='tag-documents-tab'>
            {/* Update Document Tags Section */}
            <Stack direction='column' flex={1} sx={{ position: 'relative' }}>
                {/* Loading Overlay */}
                {filesLoading && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(255, 255, 255, 0.7)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 1000,
                            border: '1px solid #1976d2',
                            borderRadius: '4px',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 1.5,
                                padding: '20px 24px',
                                backgroundColor: '#ffffff',
                                border: '2px solid #1976d2',
                                borderRadius: '8px',
                            }}
                        >
                            <CircularProgress size={28} />
                            <Typography variant="body1" sx={{ color: '#1976d2' }}>
                                Loading documents...
                            </Typography>
                        </Box>
                    </Box>
                )}
                
                {/* Update Document Tags Content */}
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="h6" gutterBottom>
                        Update Document Tags
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <CSVDownloadButton
                            generateCSVContent={() => generateDocTagsCSV(true)}
                            filename={'document_tags'}
                            tooltipText={'Download CSV with current tags and untagged documents'}
                            disabled={isDisabled}
                            size="small"
                            style={{ 
                                backgroundColor: '#e3f2fd', 
                                borderRadius: '8px'
                            }}
                            label="All Docs"
                        />
                        <CSVDownloadButton
                            generateCSVContent={() => generateDocTagsCSV(false)}
                            filename={'document_tags'}
                            tooltipText={'Download CSV with current tags, tagged documents only'}
                            disabled={isDisabled}
                            size="small"
                            style={{ 
                                backgroundColor: '#f3e5f5', 
                                borderRadius: '8px'
                            }}
                            label="Tagged Docs"
                        />
                        <CSVUploadButton
                            schema={docTagsCsvSchema}
                            onDataLoaded={handleDocTagsCSVUpload}
                            tooltipText={'Upload CSV to assign tags'}
                            disabled={isDisabled}
                            size="small"
                        />
                    </Stack>
                </Stack>
                <Divider/>
                
                <Stack direction='column' spacing={4} marginTop={3}>
                    <Stack direction='column' spacing={2} sx={{ flex: 1 }}>
                            {/* Select Documents */}
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" color="textSecondary" sx={{ marginBottom: 1, fontWeight: 500 }}>
                                    Select Documents
                                </Typography>
                                <SIAutocompleteMultiSelect
                                    key={`documents-${resetKey}`}
                                    items={documentSelectionItems}
                                    limitTags={1}
                                    onSelect={(selectedItems) => {
                                        const docs = findDocumentsBySelectionItems(selectedItems || []);
                                        setSelectedDocuments(docs);
                                        // Clear tags to remove when documents change
                                        setTagsToRemove([]);
                                    }}
                                    disabled={isDisabled}
                                    style={{ width: '450px' }}
                                    loadProgress={loadProgress}
                                    pageSize={200}
                                />
                            </Box>

                            {/* Select Tags to Add */}
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" color="textSecondary" sx={{ marginBottom: 1, fontWeight: 500 }}>
                                    Tags to Add
                                </Typography>
                                <SIAutocompleteMultiSelect
                                    key={`tags-add-${resetKey}`}
                                    items={activeTagSelectionItems}
                                    limitTags={1}
                                    onSelect={(selectedItems) => {
                                        const tags = findTagsBySelectionItems(selectedItems || [], activeTagValueByRowId);
                                        setTagsToAdd(tags);
                                    }}
                                    disabled={isDisabled}
                                    style={{ width: '450px' }}
                                    loadProgress={loadProgress}
                                />
                            </Box>

                            {/* Select Tags to Remove */}
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" color="textSecondary" sx={{ marginBottom: 1, fontWeight: 500 }}>
                                    Tags to Remove
                                </Typography>
                                <SIAutocompleteMultiSelect
                                    key={`tags-remove-${resetKey}`}
                                    items={availableTagsToRemoveSelectionItems}
                                    limitTags={1}
                                    onSelect={(selectedItems) => {
                                        const tags = findTagsBySelectionItems(selectedItems || [], availableTagsToRemoveByRowId);
                                        setTagsToRemove(tags);
                                    }}
                                    disabled={isDisabled || selectedDocuments.length === 0}
                                    style={{ width: '450px' }}
                                    loadProgress={() => loadProgress() && selectedDocuments.length > 0}
                                    />
                            </Box>
                        </Stack>

                        {/* Update Button */}
                        <Stack direction='row' spacing={2} alignItems='flex-start'>
                            <Button
                                variant="contained"
                                onClick={handleUpdateDocumentTags}
                                disabled={isUpdateDisabled()}
                                sx={{ width: '150px' }}
                            >
                                {updateLoading ? 'Updating...' : 'Update Tags'}
                            </Button>
                        </Stack>
                </Stack>
            </Stack>

            {/* Vertical Separator */}
            <Divider orientation="vertical" flexItem sx={{ margin: '0 16px' }} />

            {/* Tag Documents Where Section */}
            <Stack direction='column' flex={1}>
                <Typography variant="h6" gutterBottom>
                    Tag Documents Where
                </Typography>
                <Divider/>
                
                <Stack direction='column' spacing={4} marginTop={3}>
                    <Stack direction='column' spacing={2} sx={{ flex: 1 }}>
                            {/* Tags to Add */}
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" color="textSecondary" sx={{ marginBottom: 1, fontWeight: 500 }}>
                                    Tags to Add
                                </Typography>
                                <SIAutocompleteMultiSelect
                                    key={`where-tags-add-${resetKey}`}
                                    items={activeTagSelectionItems}
                                    limitTags={1}
                                    onSelect={(selectedItems) => {
                                        const tags = findTagsBySelectionItems(selectedItems || [], activeTagValueByRowId);
                                        setWhereTagsToAdd(tags);
                                    }}
                                    disabled={isWhereDisabled || whereLoading}
                                    style={{ width: '450px' }}
                                    loadProgress={loadProgress}
                                />
                            </Box>

                            {/* Where Tagged With (Single Select) */}
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" color="textSecondary" sx={{ marginBottom: 1, fontWeight: 500 }}>
                                    Where Tagged With
                                </Typography>
                                <SIAutocomplete
                                    key={`where-tagged-with-${resetKey}`}
                                    items={activeTagSelectionItems}
                                    initial_selection={whereTaggedWith ? { 
                                        value: whereTaggedWith.rowid, 
                                        label: whereTaggedWith.value 
                                    } : undefined}
                                    onSelect={(item) => setWhereTaggedWith(findTagBySelectionItem(item))}
                                    disabled={isWhereDisabled || whereLoading}
                                    style={{ width: '450px' }}
                                    loadProgress={loadProgress}
                                />
                            </Box>
                    </Stack>

                        {/* Update Button */}
                        <Stack direction='row' spacing={2} alignItems='flex-start'>
                            <Button
                                variant="contained"
                                onClick={handleUpdateDocumentTagsWhere}
                                disabled={isWhereUpdateDisabled()}
                                sx={{ width: '150px' }}
                            >
                                {whereLoading ? 'Updating...' : 'Update Tags'}
                            </Button>
                        </Stack>
                </Stack>
            </Stack>

            {/* Confirmation Dialog */}
            {dialogProps && (
                <StepConfirmationWithState
                    confirmationDialogs={[dialogProps]}
                    finish={() => {}}
                    cancel={() => setDialogProps(undefined)}
                    isValid={true}
                />
            )}
        </Stack>
    );
}); 